import {useState, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {Rocket, LayoutGrid, List, Plus, Search} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Card, CardContent} from '@common-web/components/ui/card';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {PipelineKanbanBoard} from './components/PipelineKanbanBoard';
import {PipelineTableView} from './components/PipelineTableView';
import {CreateOnboardingDialog} from './components/CreateOnboardingDialog';

type ViewMode = 'kanban' | 'table';

const metricCards = [
  {key: 'totalInPipeline', label: 'Total in Pipeline', accent: 'border-l-brand-blue'},
  {key: 'avgDaysInPipeline', label: 'Avg Days', accent: 'border-l-brand-gray'},
  {key: 'atRiskCount', label: 'At Risk', accent: 'border-l-amber-500'},
  {key: 'stalledCount', label: 'Stalled', accent: 'border-l-red-500'},
  {key: 'completedThisMonth', label: 'Completed This Month', accent: 'border-l-emerald-500'},
] as const;

export function OnboardingPipelineDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [selectedClientId, setSelectedClientId] = useState(searchParams.get('clientId') || '');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Pre-fill from URL params (e.g. coming from a deal page)
  // Capture once on mount into state so clearing params doesn't lose the data
  const [prefill] = useState(() => {
    if (searchParams.get('startOnboarding') !== 'true') return null;
    return {
      groupName: searchParams.get('groupName') || '',
      rfcSubmissionId: searchParams.get('rfcId') || undefined,
      clientId: searchParams.get('clientId') || '',
    };
  });

  useEffect(() => {
    if (prefill) {
      setShowCreateDialog(true);
      // Clear the URL params so refreshing doesn't re-open
      setSearchParams({}, {replace: true});
    }
  }, []);

  const clientIdParam =
    selectedClientId && selectedClientId !== 'all' ? selectedClientId : undefined;
  const statusParam = (selectedStatus && selectedStatus !== 'all' ? selectedStatus : undefined) as
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'ON_HOLD'
    | 'CANCELLED'
    | undefined;
  const phaseParam = (selectedPhase && selectedPhase !== 'all' ? selectedPhase : undefined) as
    | 'INITIAL_SETUP'
    | 'PRE_ENROLLMENT'
    | 'ENROLLMENT'
    | 'GO_LIVE_PREP'
    | 'POST_LAUNCH'
    | undefined;
  const searchParam = searchQuery || undefined;

  const statsQuery = trpc.internal.onboarding.getPipelineStats.useQuery({
    clientId: clientIdParam,
  });

  const clientsQuery = trpc.internal.client.list.useQuery();

  const onboardingsQuery = trpc.internal.onboarding.listOnboardings.useQuery({
    clientId: clientIdParam,
    status: statusParam,
    phase: phaseParam,
    search: searchParam,
    page: 1,
    pageSize: 100,
  });

  const stats = statsQuery.data;

  function getStatValue(key: (typeof metricCards)[number]['key']): string {
    if (!stats) return '—';
    const value = stats[key];
    if (key === 'avgDaysInPipeline') return String(Math.round(value));
    return String(value);
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title='Group Onboarding Pipeline'
          description='Track and manage group onboarding progress'
          icon={<Rocket className='h-6 w-6' />}
          isFetching={onboardingsQuery.isFetching}
          isLoading={onboardingsQuery.isLoading}
        >
          <PageHeaderAction onClick={() => setShowCreateDialog(true)}>
            <Plus className='h-4 w-4 mr-2' />
            Start Onboarding
          </PageHeaderAction>
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          {/* Metric Cards */}
          <div className='grid grid-cols-5 gap-4 mb-6'>
            {metricCards.map((metric) => (
              <Card key={metric.key} className={`border-l-4 ${metric.accent}`}>
                <CardContent className='p-4'>
                  <p className='text-xs font-medium text-brand-gray uppercase tracking-wider'>
                    {metric.label}
                  </p>
                  <p className='text-2xl font-bold'>{getStatValue(metric.key)}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filter Bar */}
          <div className='flex items-center gap-3 mb-4'>
            <Select
              value={selectedClientId || 'all'}
              onValueChange={(val) => setSelectedClientId(val === 'all' ? '' : val)}
            >
              <SelectTrigger className='w-48'>
                <SelectValue placeholder='All Clients' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Clients</SelectItem>
                {(clientsQuery.data ?? []).map((client: {id: string; name: string}) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={selectedStatus || 'all'}
              onValueChange={(val) => setSelectedStatus(val === 'all' ? '' : val)}
            >
              <SelectTrigger className='w-40'>
                <SelectValue placeholder='All' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='IN_PROGRESS'>In Progress</SelectItem>
                <SelectItem value='COMPLETED'>Completed</SelectItem>
                <SelectItem value='ON_HOLD'>On Hold</SelectItem>
                <SelectItem value='CANCELLED'>Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={selectedPhase || 'all'}
              onValueChange={(val) => setSelectedPhase(val === 'all' ? '' : val)}
            >
              <SelectTrigger className='w-44'>
                <SelectValue placeholder='All' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='INITIAL_SETUP'>Initial Setup</SelectItem>
                <SelectItem value='PRE_ENROLLMENT'>Pre-Enrollment</SelectItem>
                <SelectItem value='ENROLLMENT'>Enrollment</SelectItem>
                <SelectItem value='GO_LIVE_PREP'>Go Live Prep</SelectItem>
                <SelectItem value='POST_LAUNCH'>Post Launch</SelectItem>
              </SelectContent>
            </Select>

            <div className='relative w-64'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray-light' />
              <Input
                className='pl-9'
                placeholder='Search groups...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className='ml-auto flex items-center gap-1'>
              <Button
                variant='ghost'
                size='icon'
                className={viewMode === 'kanban' ? 'bg-brand-blue/10 text-brand-blue' : ''}
                onClick={() => setViewMode('kanban')}
              >
                <LayoutGrid className='h-4 w-4' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className={viewMode === 'table' ? 'bg-brand-blue/10 text-brand-blue' : ''}
                onClick={() => setViewMode('table')}
              >
                <List className='h-4 w-4' />
              </Button>
            </div>
          </div>

          {/* Content Area */}
          {onboardingsQuery.isLoading ? (
            <div className='space-y-4'>
              <div className='h-8 w-48 bg-gray-200 rounded-sm animate-pulse' />
              <div className='h-64 bg-gray-100 rounded-sm animate-pulse' />
            </div>
          ) : viewMode === 'kanban' ? (
            <PipelineKanbanBoard onboardings={onboardingsQuery.data?.items ?? []} />
          ) : (
            <PipelineTableView
              onboardings={onboardingsQuery.data?.items ?? []}
              loading={onboardingsQuery.isLoading}
            />
          )}

          {/* Create Dialog */}
          <CreateOnboardingDialog
            open={showCreateDialog}
            onOpenChange={setShowCreateDialog}
            clientId={prefill?.clientId || selectedClientId || ''}
            prefill={prefill ?? undefined}
          />
        </div>
      </div>
    </div>
  );
}
